import type React from "react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useDatasources } from "src/hooks/useDatasources";
import { type ChatVectorIndex, type ChatVectorIndexShorthand, SF, updateSettings } from "../db";

interface TabContextType {
    selectedTab: ChatVectorIndex;
    selectedTabShorthand: ChatVectorIndexShorthand;
    setSelectedTab: (tab: ChatVectorIndex) => void;
    selectedSearchTab: string;
    setSelectedSearchTab: (tab: string) => void;
}

const defaultContextValue: TabContextType = {
    selectedTab: "combined",
    selectedTabShorthand: SF,
    setSelectedTab: () => {},
    selectedSearchTab: "All",
    setSelectedSearchTab: () => {},
};

const TabContext = createContext(defaultContextValue);

interface TabProviderProps {
    children: ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ children }) => {
    const [selectedTab, setSelectedTab] = useState<ChatVectorIndex>(() => {
        return "combined" as ChatVectorIndex;
    });
    const [selectedSearchTab, setSelectedSearchTab] = useState<string>(() => {
        const preferences = localStorage.getItem("chatPreferences");
        return preferences ? JSON.parse(preferences).searchTab : "All";
    });
    const { data: chatVectorIndex = [], isLoading } = useDatasources();

    useEffect(() => {
        localStorage.setItem("selectedTab", selectedTab);
        updateSettings("chat_vector_index", selectedTab);
    }, [selectedTab]);

    useEffect(() => {
        localStorage.setItem("selectedSearchTab", selectedSearchTab);
    }, [selectedSearchTab]);

    const selectedTabShorthand: ChatVectorIndexShorthand =
        chatVectorIndex.find((index) => index.value.includes(selectedTab))?.shorthand ?? "combined";

    const value = useMemo(
        () => ({
            selectedTab,
            selectedTabShorthand,
            setSelectedTab,
            selectedSearchTab,
            setSelectedSearchTab,
        }),
        [selectedTab, selectedTabShorthand, selectedSearchTab],
    );

    const isPlaywright = window.location.search.includes("isPlaywright=true");

    if (isLoading && chatVectorIndex.length === 0 && !isPlaywright) {
        return null; // Or return a loading spinner here if preferred
    }

    return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

export const useTab = () => useContext(TabContext);
